import { useEffect } from "react";
import useProctoring from "../hooks/useProctoring";
import { useInterviewRoundContext } from "../../../Context/InterviewRoundContext";

const ProctoringWatcher: React.FC = () => {
  const {
    currentSession,
    activeQuestionIndex,
    isRoundFinished,
    exitTimer,
    setExitTimer,
    setShowExitConfirmation,
    setExitFullscreenMessage,
    isFullscreen,
    setIsFullscreen,
    isVisibilityActive,
    setIsVisibilityActive,
    showExitConfirmation,
    elementRef: containerRef,
    exitConfirmationModalRef,
  } = useInterviewRoundContext();

  const {
    handleFullScreenExitAttempt,
    handleNotAllowedKeyPressing,
    handleMultipleTabOpening,
    handleCursorFocusChange,
  } = useProctoring();

  const suspiciousCombos = [
    "meta+r",
    "meta+tab",
    "meta+q",
    "meta+w",
    "control+r",
    "control+w",
    "control+tab",
    "alt+f4",
    "control+shift+i",
    "control+shift+c",
    "control+shift+j",
    "control+f4",
    "control+n",
    "meta+n",
  ];

  const plainFunctionKeysToCatch = ["F4", "F5", "F6", "F7", "F8", "F9"];

  const shouldTrigger = () =>
    currentSession &&
    currentSession.status === "pending" &&
    activeQuestionIndex >= 0 &&
    !isRoundFinished;

  // Track fullscreen and visibility state
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = document.fullscreenElement !== null;
      setIsFullscreen(isNowFullscreen);
    };

    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === "visible";
      setIsVisibilityActive(isVisible);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Detect fullscreen/tab loss after round starts
  useEffect(() => {
    if (
      (!isFullscreen || !isVisibilityActive) &&
      shouldTrigger() &&
      !exitTimer
    ) {
      setShowExitConfirmation(true);
      setExitTimer(true);
      setExitFullscreenMessage(
        "You exited full screen or switched tabs. Please stay on the interview screen."
      );
    }
  }, [isFullscreen, isVisibilityActive, isRoundFinished]);

  // Suspicious key press detection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const rawKey = e.key;
      const key = rawKey.toLowerCase();

      if (!shouldTrigger()) return;

      if (key === "escape" && currentSession) {
        e.preventDefault();
        e.stopPropagation();
        setShowExitConfirmation(true);
        setExitFullscreenMessage("Suspicious key press: Escape");
        handleNotAllowedKeyPressing(currentSession, ["Escape"]);
        return;
      }

      if (
        plainFunctionKeysToCatch.includes(rawKey.toUpperCase()) &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.metaKey &&
        !e.shiftKey &&
        currentSession
      ) {
        e.preventDefault();
        e.stopPropagation();
        setShowExitConfirmation(true);
        setExitFullscreenMessage(`Suspicious function key: ${rawKey}`);
        handleNotAllowedKeyPressing(currentSession, [rawKey]);
        return;
      }

      if (["control", "meta", "alt", "shift"].includes(key)) return;

      const modifiers = [];
      if (e.ctrlKey) modifiers.push("control");
      if (e.metaKey) modifiers.push("meta");
      if (e.altKey) modifiers.push("alt");
      if (e.shiftKey) modifiers.push("shift");

      if (modifiers.length === 0) return;

      const keyCombo = [...modifiers, key].join("+");

      if (
        suspiciousCombos.includes(keyCombo) &&
        !showExitConfirmation &&
        currentSession
      ) {
        e.preventDefault();
        e.stopPropagation();
        setShowExitConfirmation(true);
        setExitFullscreenMessage(`Suspicious key combination: ${keyCombo}`);
        handleNotAllowedKeyPressing(currentSession, [keyCombo]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentSession, activeQuestionIndex, isRoundFinished]);

  // Blur (cursor out of focus)
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      setTimeout(() => {
        const relatedTarget = e.relatedTarget as Node | null;

        const isInModal =
          exitConfirmationModalRef?.current &&
          relatedTarget &&
          exitConfirmationModalRef.current.contains(relatedTarget);

        if (
          !isInModal &&
          shouldTrigger() &&
          !showExitConfirmation &&
          currentSession
        ) {
          setShowExitConfirmation(true);
          setExitFullscreenMessage(
            "Detected Mouse cursor left the round window."
          );
          handleCursorFocusChange(currentSession);
        }
      }, 50);
    };

    const container = containerRef?.current;
    if (container) {
      container.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (container) {
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, [
    currentSession,
    activeQuestionIndex,
    isRoundFinished,
    showExitConfirmation,
    exitConfirmationModalRef,
  ]);

  // // Tab switch / visibility loss
  useEffect(() => {
    const handleVisibilityLoss = () => {
      if (
        document.hidden &&
        shouldTrigger() &&
        !showExitConfirmation &&
        currentSession
      ) {
        setShowExitConfirmation(true);
        setExitFullscreenMessage("Switched tab or minimized browser.");
        handleMultipleTabOpening(currentSession);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityLoss);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityLoss);
    };
  }, [currentSession, activeQuestionIndex, isRoundFinished]);

  return null;
};

export default ProctoringWatcher;
